import { baseApiPath, get, post, put, deleteEntity } from "./base-api";
import { IPromptsResponse, IPrompt, IPromptPayload } from "../data-access";

const getPrompts = (): Promise<IPromptsResponse> => {
  return get(`${baseApiPath}/api/chat-gpt/prompts`);
};

const createPrompt = (data: IPromptPayload): Promise<{ data: IPrompt }> => {
  return post(`${baseApiPath}/api/chat-gpt/prompts`, data);
};

const updatePrompt = (
  id: number,
  data: IPromptPayload
): Promise<{ data: IPrompt }> => {
  return put(`${baseApiPath}/api/chat-gpt/prompts/${id}`, data);
};

const deletePrompt = (id: number): Promise<void> => {
  return deleteEntity(`${baseApiPath}/api/chat-gpt/prompts/${id}`);
};

const setDefaultPrompt = (id: number): Promise<void> => {
  return put(`${baseApiPath}/api/ai/prompts/${id}/set-default`, {});
};

export interface ITestTemplateJob {
  title: string;
  description: string;
  budget: string;
  location: string;
  duration: string;
  skills: string[];
  payment_type: string;
  experience_level: string;
  category: string;
  workload: string;
  proposals: string;
  connects: string;
  is_premium: string;
  client: {
    name: string;
    country: string;
    city: string;
    payment_verified: string;
    total_spent: string;
    hire_rate: string;
    rating: string;
    member_since: string;
    total_reviews: string;
    jobs_posted: string;
    jobs_open: string;
  };
}

export interface ITestTemplateResponse {
  job: ITestTemplateJob;
  cover_letter: string;
  resolved_vars: { variable: string; value: string }[];
  job_data: Record<string, unknown>;
}

const improveTemplate = (
  currentTemplate: string
): Promise<{ prompt: string; name: string; changes: string[] }> => {
  return post(`${baseApiPath}/api/ai/prompts/improve-template`, {
    template: currentTemplate,
  });
};

const testTemplate = (
  template: string,
  jobData?: Record<string, unknown>
): Promise<ITestTemplateResponse> => {
  return post(`${baseApiPath}/api/ai/prompts/test-template`, {
    template,
    ...(jobData ? { job_data: jobData } : {}),
  });
};

export interface ITemplateStats {
  sent: number;
  bids: number;
  replies: number;
  reply_rate: number;
  offers: number;
}

export interface IPromptsStatsResponse {
  per_template: Record<string, ITemplateStats>;
  total: {
    proposals: number;
    bids: number;
    replies: number;
    reply_rate: number;
    offers: number;
  };
}

const getPromptsStats = (params?: string): Promise<IPromptsStatsResponse> => {
  const queryParams = params ? `?${params}` : "";
  return get(`${baseApiPath}/api/ai/prompts-stats${queryParams}`);
};

// ── Template Data ──

export interface ITemplateDataItemResponse {
  id: number;
  name: string;
  value: string;
  sort_order: number;
}

const getTemplateData = (): Promise<{ data: ITemplateDataItemResponse[] }> => {
  return get(`${baseApiPath}/api/ai/template-data`);
};

const syncTemplateData = (
  items: { name: string; value: string }[]
): Promise<{ data: ITemplateDataItemResponse[] }> => {
  return put(`${baseApiPath}/api/ai/template-data`, { items });
};

export const promptsApi = {
  getPrompts,
  createPrompt,
  updatePrompt,
  deletePrompt,
  setDefaultPrompt,
  improveTemplate,
  testTemplate,
  getPromptsStats,
  getTemplateData,
  syncTemplateData,
};
